import CoopPriceProjectionInterface from "@app/Interface/CoopPriceProjectionInterface";
import axios from "@app/Security/AxiosAuthentication";
import ApiEndpoints from "@app/utils/ApiEndpoints";
import RollbarHelper from "@app/utils/RollbarHelper";

export default class CoopPriceProjectionHelper {

    /**
     * Gets coop rpice projection data.
     * reportType = milkPricing:      gets all records
     *              region:           region code for use with calculator
     *              pricingMonthYear: month/year values for drop-down
     *              seedprice:        used with calculator
     *              seedWeightedAvg:  used with calculator
     * 
     * @param controller AbortController - Handles when component is unmounted
     * @param fpuId      Currently selected FPU
     * @param pageId     Page ID is used to determine permission access
     * @param reportType region | pricingMonthYear | seedPrice | seedWeightedAvg | milkPricing
     * @returns 
     */
    static getPriceProjections = async (controller: AbortController, fpuId: number, pageId: number, reportType: string): Promise<CoopPriceProjectionInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        let endPoint: string = ApiEndpoints.COOP_PRICE_PROJECTION + `?page=1&fpu_id=${fpuId}&page_id=${pageId}`;

        if (reportType) {
            endPoint += `&report_type=${reportType}`;
        }

        const url = baseUrl + endPoint;

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                // TODO: Each report type returns a 
                //       different set of data. will need to 
                //       check for different report type and return 
                //       different interfaces
                const components: CoopPriceProjectionInterface[] = response.data["hydra:member"];

                return components;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "COOP getPriceProjections()");
                
                // return an empty set of navItems
                const emptyItems: Array<CoopPriceProjectionInterface> = [];

                return emptyItems;

            });

    }

    /**
     * Filters the rows down to the limit, starting on the month modifier
     * If monthModifier = -1, start with previous month
     * limit = 5, get 5 records starting with the monthModifier
     * 
     * @param rows          original rows
     * @param limit         number of records to return
     * @param monthModifier modifier todays month. Determines which record to start with
     * @returns 
     */
    static getRowsForDashboard = (rows: CoopPriceProjectionInterface[], limit: number, monthModifier: number): CoopPriceProjectionInterface[] => {

        const filteredRows: CoopPriceProjectionInterface[] = [];

        let foundCount = 0;

        const currentDate = new Date();

        // NOTE: currentDate.getMonth() is the index not the actual month 
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthModifier, 1);
       
        rows.forEach(row => {

            // NOTE: Date(Year, month index!!!, day); 
            const rowDate = new Date(row.PricingYear, row.PricingMonth - 1, 1);

            if (foundCount <= limit && startDate <= rowDate) {
                
                foundCount++;

                // set DisplayMonthYear
                row.MonthYearDisplay = CoopPriceProjectionHelper.getMonthYearDisplay(row);

                filteredRows.push(row);
            }

        });

        return filteredRows;

    }

    /**
     * gets the display for month year as Jan, 2021
     * 
     * @param data 
     * @returns 
     */
    static getMonthYearDisplay = (data: CoopPriceProjectionInterface): string => {

        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec"
        ];

        const displayMonth = months[data.PricingMonth - 1];

        const displayValue = `${displayMonth}, ${data.PricingYear}`;

        return displayValue;
    }

}